import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "../assets/styles/PageStyles.scss";
import Breadcrumbs from "../components/Breadcrumbs";

function TermsConditions() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const termsOfUseContent = [
    {
      title: "Goal Guru – Terms and Conditions",
      content:
        'Effective Date: July 11, 2026\n\nWelcome to Goal Guru! By using our mobile application ("App"), you agree to comply with and be bound by these Terms and Conditions. If you do not agree, please discontinue using the App.',
    },
    {
      title: "1. Acceptance of Terms",
      content:
        "By accessing or using Goal Guru, you agree to these Terms and our Privacy Policy. Continued use after updates constitutes acceptance of the revised Terms.",
    },
    {
      title: "2. Description of Service",
      content:
        "Goal Guru provides real-time football match updates, statistics, team pages, personalized notifications, and community features such as in-app chat. Some features and functionalities are available only through paid subscription plans.",
    },
    {
      title: "3. Accounts and Sign-In",
      content:
        "You may use Google Sign-In to create or access your account. You are responsible for providing accurate information and maintaining the security of your credentials. Any activity under your account is your responsibility.",
    },
    {
      title: "4. Subscriptions and Payments",
      content: [
        "Goal Guru offers paid subscription plans that grant access to premium features within the App.",
        "Subscriptions are billed through Google Play or the App Store, depending on your device.",
        "Payments, renewals, and cancellations are managed through your respective app store account settings.",
        "Subscriptions automatically renew unless canceled at least 24 hours before the renewal date.",
        "Refunds are handled according to the refund policy of the platform used for purchase. Goal Guru does not issue refunds directly.",
        "We may change our pricing or subscription structure at any time, with notice provided through the App or our website.",
      ],
    },
    {
      title: "5. Notifications and Smart Alerts",
      content:
        "Goal Guru allows users to receive notifications about football matches and related events. While we strive to deliver them promptly, we do not guarantee real-time accuracy or delivery due to technical or third-party limitations.",
    },
    {
      title: "6. AI Chatbot",
      content:
        "Goal Guru provides an AI-powered chatbot designed to help users use the App more effectively.\n\nBy using the chatbot, you acknowledge that conversations may be stored and processed to improve chatbot performance, accuracy, and user experience.\n\nUsers should not share sensitive personal information through the chatbot. The chatbot does not provide professional, legal, or betting advice, and responses are for informational purposes only.",
    },
    {
      title: "7. User Conduct",
      content:
        "You agree not to use the App for unlawful, abusive, or disruptive purposes, or to attempt unauthorized access to our systems. This includes your behavior in community spaces such as chat rooms, where you are expected to interact respectfully with other users. Violation of this section may result in suspension or termination of your access.",
    },
    {
      title: "8. Community Chat and User-Generated Content",
      content: [
        "Goal Guru includes community spaces, such as in-app chat rooms, where you can post messages that are visible to other users.",
        "You are solely responsible for the content you post. Do not post content that is unlawful, hateful, harassing, threatening, sexually explicit, spam, misleading, or that infringes the rights of others — including sharing another person's personal information without consent.",
        "By posting content in community spaces, you grant Goal Guru a non-exclusive, worldwide, royalty-free license to store, display, and distribute that content within the App for the purpose of operating the community features.",
        "You can report inappropriate messages or users directly in the App. Reports are reviewed, and we may remove any content at our discretion.",
        "We may issue warnings, remove content, mute, temporarily suspend, or permanently ban accounts that violate these rules or these Terms — with or without prior notice.",
        "Community content reflects the views of the users who post it, not of Goal Guru. We do not endorse and are not responsible for user-generated content, but we act on valid reports.",
        "Do not share sensitive personal information (yours or anyone else's) in community spaces.",
      ],
    },
    {
      title: "9. Intellectual Property",
      content:
        "All content, branding, and technology within Goal Guru are owned by us or our licensors. You may not copy, modify, or redistribute any part of the App without prior written consent.",
    },
    {
      title: "10. Third-Party Services",
      content:
        "Goal Guru uses trusted third-party services such as Google services (for analytics, notifications, performance monitoring, AI processing, and data storage) and RevenueCat (for subscription management). Your use of these services is subject to their respective terms and privacy policies.",
    },
    {
      title: "11. Disclaimer of Warranties",
      content:
        'The App is provided "as is" without any warranties, express or implied. We do not guarantee uninterrupted service, accuracy, or reliability of the information provided.',
    },
    {
      title: "12. Limitation of Liability",
      content:
        "To the fullest extent permitted by law, Goal Guru and its developers shall not be liable for any indirect, incidental, consequential, or punitive damages arising from your use or inability to use the App, including damages arising from user-generated content posted by other users.",
    },
    {
      title: "13. Modifications to Terms",
      content:
        "We may update these Terms periodically. Updates will become effective once published within the App or on our website.",
    },
    {
      title: "14. Termination",
      content:
        "We reserve the right to suspend or terminate your access to the App at any time, with or without notice, for conduct that violates these Terms — including the community rules in Section 8 — or for any other legitimate reason. Bans from community features may be applied independently of, or in addition to, account termination.",
    },
    {
      title: "15. Governing Law",
      content:
        "These Terms are governed by the laws of the Netherlands. Any disputes arising under these Terms shall be resolved in the courts of the Netherlands.",
    },
    {
      title: "16. Odds Disclaimer",
      content:
        "Goal Guru may display average football match odds solely for informational and entertainment purposes. The App does not promote or facilitate betting or gambling. Users are responsible for complying with local laws and regulations regarding sports betting.",
    },
    {
      title: "17. Contact Information",
      content:
        "If you have any questions about these Terms, please contact us at:\nEmail: support@goalguru.live",
    },
  ];

  return (
    <div className="page-container">
      <Helmet>
        <title>Terms & Conditions | Goal Guru</title>
        <meta
          name="description"
          content="Read Goal Guru's Terms & Conditions for using our services, subscriptions, and smart football notifications."
        />
        <link rel="canonical" href="https://goalguru.live/terms-conditions/" />
        <meta property="og:title" content="Terms & Conditions | Goal Guru" />
        <meta
          property="og:description"
          content="Read Goal Guru's Terms & Conditions for using our services, subscriptions, and smart football notifications."
        />
        <meta
          property="og:url"
          content="https://goalguru.live/terms-conditions/"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://goalguru.live/og/terms-conditions.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms & Conditions | Goal Guru" />
        <meta
          name="twitter:description"
          content="Read Goal Guru's Terms & Conditions for using our services, subscriptions, and smart football notifications."
        />
        <meta
          name="twitter:image"
          content="https://goalguru.live/og/terms-conditions.png"
        />
      </Helmet>
      <Breadcrumbs items={[{ name: "Terms & Conditions" }]} />
      <h1>Terms & Conditions</h1>
      {termsOfUseContent.map((section, index) => (
        <div key={index}>
          <h2>{section.title}</h2>
          {Array.isArray(section.content) ? (
            <ul>
              {section.content.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          ) : (
            section.content
              .split("\n\n")
              .map((paragraph, i) => <p key={i}>{paragraph}</p>)
          )}
        </div>
      ))}
    </div>
  );
}

export default TermsConditions;
